import { TextFormat } from "../TextFormat";
import { TextFormatAlign } from "../TextFormatAlign";
// import { TextFieldAutoSize } from "../TextFieldAutoSize";
import { RichTextInputToken, RichTextDrawTokens, FontRestrictionType, RichTextPrintTokens } from "./dataTypes";
import { TEXT_GUTTER_WIDTH, FALLBACK_TEXT_SIZE } from "./defaults";
import { applyFormat } from "./applyFormat";

const trimStart = require("lodash.trimstart");
const trimEnd = require("lodash.trimend");

/**
 * Whether to break individual words if their length exceeds the line width
 */
const WORD_BREAK = true;

const WORD_BREAK_PUNCTUATION = new Set([
    ".",
    ":",
    ";",
    ",",
    "!",
    "?",
    ")",

    // Japanese
    "。",
    "！",
    "？"
]);

export type LayedOutTextMetrics = {
    readonly textWidth: number,
    readonly textHeight: number,
    readonly totalNumberOfTextLines: number,
    readonly maximumNumberOfVisibleLines: number,
    readonly drawnLines: string[],
    readonly lineHeight: number,
    readonly distanceToBaseline: number;
    readonly leading: number;
    readonly widthUntilEndOfText: number;
    readonly textWithGutterWidth: number;
    readonly textWithGutterHeight: number;
};

export type LayedOutText = {
    readonly drawingTokens: RichTextDrawTokens[],
    readonly metrics: LayedOutTextMetrics;
};

import flatten = require("lodash.flatten");

/**
 * Aligns the tokens into their respective lines for multiline and centered fields
 *
 * This might in turn mean breaking up `plain` text token into multiple chunks to be drawn across lines
 */
export function layOutTokens(this: void, ctx: CanvasRenderingContext2D, tokens: ReadonlyArray<RichTextInputToken>, initialFormat: TextFormat, maxWidth: number | "textWidth", fontRestrictions: FontRestrictionType | null, multiline: boolean, wordWrap: boolean, maxHeight: number, firstVisibleLineIndex: number, verticalAlign: "top" | "middle" | "bottom"): LayedOutText {
    const distanceToBaseline = (initialFormat.size || 12) - 1; // for vertical alignment with Flash font rendering
    const leading = multiline && initialFormat.leading || 0;
    //Round the lineHeight to prevent different values for the html and canvas text if browser does not sub pixel lineHeight (e.g. chrome)
    const lineHeight = Math.round(((distanceToBaseline + 1) * 1.2) + leading); // 1.2 since Flash seems to measure the fontSize for the ascent, not em square (as per the measurements in http://www.smarttutorials.net/how-to-find-text-width-of-a-textfield-in-action-script-flash-professional-cs6/); - we might want to derive this value by printing and measuring something similar to the em square, like `"I"` and `"g"`
    const initialYOffset = -(Math.max(firstVisibleLineIndex, 1) - 1) * lineHeight;

    let currentLineTokens: RichTextPrintTokens[] = [];
    let tokensByLine: RichTextPrintTokens[][] = [currentLineTokens];

    // subtract gutter width before layout
    if (maxWidth !== "textWidth") {
        maxWidth -= TEXT_GUTTER_WIDTH;
    }
    maxHeight -= 2 * TEXT_GUTTER_WIDTH;

    let xOffset = 0;
    let currentFormat = initialFormat;
    let previousFormats: TextFormat[] = [];

    for (const token of tokens) {
        switch (token.type) {
            case "newline":
                currentLineTokens.push({type: "plain", text: "\n", width: 0});
                currentLineTokens = [];
                tokensByLine.push(currentLineTokens);

                xOffset = 0;
                break;
            case "plain":
                {
                    applyFormat(ctx, currentFormat, fontRestrictions);
                    const text = token.text;

                    const chunkWidth: number = ctx.measureText(text).width;

                    if (wordWrap && typeof maxWidth === "number" && xOffset + chunkWidth > maxWidth) {
                        // chunk is longer than remaining space in line, needs to be broken up word by word
                        let words: string[] = text.split(/([ \t\n\x0B\f\r])/); // specify at which characters to break; excluding non-breaking spaces (which would be included in `\s`)
                        words = words.filter(word => word != "");

                        if (WORD_BREAK) {
                            // split all words that are longer than the `maxWidth` into individual characters, as they wouldn't fit on any line
                            words = words.reduce(
                                (acc, word) => {
                                    const wordWidth = ctx.measureText(word).width;
                                    if (wordWidth > maxWidth) {
                                        // word is longer than a line, break up into characters
                                        const characters = word.split(``).reduce((acc, char) => {
                                            if (WORD_BREAK_PUNCTUATION.has(char) && acc.length > 0) {
                                                // punctuation should never stand alone, so append to previous character
                                                acc[acc.length - 1] += char;
                                            }  else {
                                                acc.push(char);
                                            }

                                            return acc;
                                        }, [] as string[]);

                                        acc.push(...characters);
                                    } else {
                                        acc.push(word);
                                    }

                                    return acc;
                                }, [] as string[]);
                        }

                        for (const word of words) {
                            const wordWidth = ctx.measureText(word).width;
                            if (xOffset + wordWidth > maxWidth && word != " ") {
                                // not enough space in current line, print newline first
                                // don't print newline if last character is a space

                                currentLineTokens = [];
                                tokensByLine.push(currentLineTokens);

                                xOffset = 0;
                            }

                            let token: RichTextDrawTokens = {
                                type: "plain",
                                text: word,
                                width: wordWidth,
                            };

                            currentLineTokens.push(token);

                            xOffset = xOffset + token.width;
                        }
                    } else {
                        currentLineTokens.push({
                            type: "plain",
                            text: text,
                            width: chunkWidth
                        });

                        xOffset = xOffset + chunkWidth;
                    }
                }
                break;

            case "push-format-augmentation": {
                // save the currentFormat, then set it to the new one
                previousFormats = [...previousFormats, currentFormat];
                currentFormat = { ...currentFormat, ...token.partialFormat };

                currentLineTokens.push({
                    type: "switch-to-format",
                    format: currentFormat,
                });
                break;
            }

            case "pop-last-format-augmentation": {
                currentFormat = previousFormats[previousFormats.length - 1];
                previousFormats = previousFormats.slice(0, previousFormats.length - 1);

                currentLineTokens.push({
                    type: "switch-to-format",
                    format: currentFormat,
                });

                break;
            }

            default:
                currentLineTokens.push(token);
                break;
        }
    }

    const maxDrawnTextWidth = Math.max(...tokensByLine.map(lineTextWidth));

    maxWidth = typeof maxWidth === "number" ? maxWidth : maxDrawnTextWidth;

    const textFieldWidth = maxWidth + 2 * TEXT_GUTTER_WIDTH;

    const drawingTokensByLine: RichTextDrawTokens[][] = tokensByLine.map((line, i) => {
        let initialXOffset: number;
        if (currentFormat.align === TextFormatAlign.CENTER) {
            const totalLineTextWidth = lineTextWidth(line);
            initialXOffset = (textFieldWidth - totalLineTextWidth) / 2;
        } else if (currentFormat.align === TextFormatAlign.RIGHT) {
            const totalLineTextWidth = lineTextWidth(line);
            initialXOffset = textFieldWidth - totalLineTextWidth - TEXT_GUTTER_WIDTH;
        } else {
            initialXOffset = TEXT_GUTTER_WIDTH;
        }

        const newLines: RichTextDrawTokens[] = [...line];
        newLines.unshift({
            type: "set-x-y",
            x: initialXOffset,
            y: initialYOffset + (i * lineHeight) + TEXT_GUTTER_WIDTH,
        });
        return newLines;
    });

    const drawnLines: string[] = drawingTokensByLine.map((line): string => {
        return line.map(token => {
            if (token.type === "plain") {
                return token.text;
            } else {
                return '';
            }
        }).join('');
    });

    const widthUntilEndOfText = Math.max(...drawingTokensByLine.map(line => {
        return line.reduce((sum, currToken) => {
            if (currToken.type === "plain") {
                return sum + currToken.width;
            } else if (currToken.type === "advance-x") {
                return sum + currToken.amount;
            } else if (currToken.type === "set-x-y") {
                return currToken.x; // Not perfect, but works in our case; ideally would branch out, then use max (imagine `\r` case)
            } else {
                return sum;
            }
        }, 0);
    }));

    const textWidth = Math.ceil(maxDrawnTextWidth);
    const textHeight = Math.ceil(drawnLines.length * lineHeight);

    const metrics: LayedOutTextMetrics = {
        textWidth: textWidth,
        textHeight: textHeight,
        totalNumberOfTextLines: drawnLines.length,
        maximumNumberOfVisibleLines: maxHeight / lineHeight,
        drawnLines: drawnLines,
        lineHeight: lineHeight,
        distanceToBaseline: distanceToBaseline,
        leading: leading,
        widthUntilEndOfText: widthUntilEndOfText,
        textWithGutterWidth: textWidth + 2 * TEXT_GUTTER_WIDTH,
        textWithGutterHeight: textHeight + 2 * TEXT_GUTTER_WIDTH,
    };

    const flattenedTokens = flatten(drawingTokensByLine)
    .reduce((accum, curr) => {
        /** combine successive `plain` tokens into one */
        const prevIndex = accum.length - 1;
        const prev = accum[prevIndex];

        if (prev && prev.type === "plain" && curr.type === "plain") {
            accum[accum.length - 1] = {
                type: "plain",
                text: prev.text + curr.text,
                width: prev.width + curr.width,
            };
        } else {
            accum.push(curr);
        }

        return accum;
    }, [] as RichTextDrawTokens[])
    .map((function () {
        /**
         * either returns a function to update the `y`-value,
         * or an identity function in case nothing needs to be mapped
         */
        if (verticalAlign === "middle") {
            const textFieldHeight = maxHeight;
            const textContentHeight = textHeight;
            const remainingHeight  = maxHeight - textHeight;
            if (remainingHeight > 0) {
                // alignedTokens = flattenedTokens
                return (token: RichTextDrawTokens) => {
                    switch (token.type) {
                        case "set-x-y":
                            return {
                                ...token,
                                y: token.y + remainingHeight / 2,
                            };
                        default:
                            return token;
                    }
                };
            }
        } else if (verticalAlign === "bottom") {
            const textFieldHeight = maxHeight;
            const textContentHeight = textHeight;
            const remainingHeight  = maxHeight - textHeight;
            if (remainingHeight > 0) {
                // alignedTokens = flattenedTokens
                return (token: RichTextDrawTokens) => {
                    switch (token.type) {
                        case "set-x-y":
                            return {
                                ...token,
                                y: token.y + remainingHeight,
                            };
                        default:
                            return token;
                    }
                };
            }
        }

        return (t: RichTextDrawTokens) => t;
    })())
    .map(t => {
        /** Align coordinates to the nearest pixel, to get less antialiasing */
        switch (t.type) {
            case "advance-x":
                return {
                    ...t,
                    amount: Math.round(t.amount),
                };
            case "set-x-y":
                return {
                    ...t,
                    x: Math.round(t.x),
                    y: Math.round(t.y),
                };
            case "plain":
            return {
                ...t,
                width: Math.round(t.width),
            };
        }

        return t;
    });

    return { drawingTokens: flattenedTokens, metrics: metrics };
}

function lineTextWidth(lineTokens: RichTextDrawTokens[]): number {
    return lineTokens.reduce((sum, currToken) => {
        if (currToken.type === "plain") {
            return sum + currToken.width;
        } else {
            return sum;
        }
    }, 0);
}
